// Paiements (F-049, F-051) : registre, saisie, correction d'affectation, annulation tracée, import de relevé CSV.
import { useState } from "react";
import { post, put, upload } from "@core/api";
import { useGet } from "@core/hooks";
import { date, dateTime, money } from "@core/format";
import { Badge, Button, Card, Checkbox, Empty, ErrorBox, FileDrop, Input, Loading, Modal, MoneyInput, Notice, PageHeader, Select, Table, Tabs } from "@core/ui";
import { PaymentModal } from "./loyers";
import { TransactionsToMatch } from "./rapprochement";
import { Amount, DocLink, LeaseSelect, METHOD_LABELS, MonthPicker, SOURCE_LABELS, currentMonth, errMessage, useAction, useG, useLeases } from "./shared";

type Payment = {
  id: string;
  lease_id: string;
  amount_cents: number;
  received_on: string;
  method: string;
  source: string;
  payer: string;
  reference: string;
  note: string;
  cancelled_at: string | null;
  property_label: string;
  tenants: string | null;
  allocated_cents: number;
};

export function PaiementsPage() {
  const [tab, setTab] = useState<"registre" | "import" | "rapprocher">("registre");
  return (
    <div className="page">
      <PageHeader eyebrow="Gestion" title="Paiements" intro="Saisissez un paiement, importez un relevé ou validez les rapprochements proposés. Rien n’est débité ni encaissé par Bailivo." />
      <Tabs
        value={tab}
        onChange={setTab}
        items={[
          { value: "registre", label: "Registre des paiements" },
          { value: "import", label: "Importer un relevé CSV" },
          { value: "rapprocher", label: "Opérations à rapprocher" },
        ]}
      />
      {tab === "registre" ? <Registre /> : null}
      {tab === "import" ? <ImportCsv onDone={() => setTab("rapprocher")} /> : null}
      {tab === "rapprocher" ? <TransactionsToMatch /> : null}
    </div>
  );
}

function Registre() {
  const { g, can } = useG();
  const [month, setMonth] = useState(currentMonth());
  const leases = useLeases();
  const q = useGet<{ items: Payment[] }>(g(`/payments?month=${month}`));
  const [newFor, setNewFor] = useState("");
  const [open, setOpen] = useState(false);
  const [cancel, setCancel] = useState<Payment | null>(null);
  const [realloc, setRealloc] = useState<Payment | null>(null);
  return (
    <Card title="Paiements reçus" actions={<MonthPicker value={month} onChange={setMonth} />}>
      {can("finance") ? (
        <div className="form-row">
          <LeaseSelect leases={leases.data?.items ?? []} value={newFor} onChange={setNewFor} label="Bail concerné" />
          <Button disabled={!newFor} onClick={() => setOpen(true)}>
            Enregistrer un paiement
          </Button>
        </div>
      ) : null}
      {q.isLoading ? <Loading /> : null}
      <ErrorBox error={q.error} />
      {q.data && q.data.items.length === 0 ? <Empty title="Aucun paiement ce mois-ci" /> : null}
      {q.data && q.data.items.length ? (
        <Table caption="Paiements du mois">
          <thead>
            <tr>
              <th>Date</th>
              <th>Logement</th>
              <th>Mode et origine</th>
              <th className="right">Montant</th>
              <th className="right">Affecté</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {q.data.items.map((p) => (
              <tr key={p.id}>
                <td>{date(p.received_on)}</td>
                <td>
                  {p.property_label}
                  <span className="block small muted">{p.payer || p.tenants}</span>
                </td>
                <td>
                  {METHOD_LABELS[p.method] ?? p.method} · {SOURCE_LABELS[p.source] ?? p.source}
                  {p.cancelled_at ? (
                    <span className="block">
                      <Badge tone="erreur">Annulé le {dateTime(p.cancelled_at)}</Badge>
                    </span>
                  ) : null}
                  {p.note ? <span className="block small muted">{p.note}</span> : null}
                </td>
                <td className="right">
                  <Amount cents={p.amount_cents} />
                </td>
                <td className="right">
                  <Amount cents={p.allocated_cents} />
                  {p.amount_cents > p.allocated_cents && !p.cancelled_at ? <span className="block small muted">Crédit : {money(p.amount_cents - p.allocated_cents)}</span> : null}
                </td>
                <td className="right">
                  {can("finance") && !p.cancelled_at ? (
                    <div className="gestion-actions">
                      <Button variant="discret" small onClick={() => setRealloc(p)}>
                        Corriger l’affectation
                      </Button>
                      <Button variant="danger" small onClick={() => setCancel(p)}>
                        Annuler
                      </Button>
                    </div>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : null}
      {open && newFor ? <PaymentModal leaseId={newFor} onClose={() => setOpen(false)} /> : null}
      {cancel ? <CancelModal payment={cancel} onClose={() => setCancel(null)} /> : null}
      {realloc ? <ReallocModal payment={realloc} onClose={() => setRealloc(null)} /> : null}
    </Card>
  );
}

function CancelModal({ payment, onClose }: { payment: Payment; onClose: () => void }) {
  const { g } = useG();
  const { busy, run } = useAction();
  const [reason, setReason] = useState("");
  return (
    <Modal
      open
      onClose={onClose}
      title="Annuler un paiement"
      footer={
        <>
          <Button variant="discret" onClick={onClose}>
            Retour
          </Button>
          <Button
            variant="danger"
            busy={busy === "c"}
            disabled={!reason.trim()}
            onClick={async () => {
              const r = await run("c", () => post<any>(g(`/payments/${payment.id}/cancel`), { reason }), (x: any) => (x.voided_receipts?.length ? "Paiement annulé ; la quittance concernée est annulée avec motif (document conservé)." : "Paiement annulé."));
              if (r) onClose();
            }}
          >
            Annuler le paiement
          </Button>
        </>
      }
    >
      <p>
        Paiement de {money(payment.amount_cents)} du {date(payment.received_on)}. Il restera visible, barré, avec son motif ; les échéances concernées redeviennent dues.
      </p>
      <Input label="Motif" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Ex. : retour de virement, erreur de saisie" required />
    </Modal>
  );
}

function ReallocModal({ payment, onClose }: { payment: Payment; onClose: () => void }) {
  const { g } = useG();
  const { busy, run } = useAction();
  const q = useGet<any>(g(`/leases/${payment.lease_id}/ledger`));
  const [values, setValues] = useState<Record<string, number | null>>({});
  const [reason, setReason] = useState("");
  const current = (q.data?.payments ?? []).find((p: any) => p.id === payment.id);
  const currentAlloc: Record<string, number> = {};
  (current?.allocations ?? []).forEach((a: any) => (currentAlloc[a.rent_call_id] = a.amount_cents));
  const calls = (q.data?.calls ?? []).filter((c: any) => c.status !== "annule" && (c.outstanding_cents > 0 || currentAlloc[c.id]));
  const val = (id: string) => (id in values ? values[id] ?? 0 : currentAlloc[id] ?? 0);
  const total = calls.reduce((s: number, c: any) => s + val(c.id), 0);
  return (
    <Modal
      open
      onClose={onClose}
      title="Corriger l’affectation"
      footer={
        <>
          <Button variant="discret" onClick={onClose}>
            Retour
          </Button>
          <Button
            busy={busy === "r"}
            disabled={!reason.trim() || total > payment.amount_cents}
            onClick={async () => {
              const allocations = calls.filter((c: any) => val(c.id) > 0).map((c: any) => ({ rent_call_id: c.id, amount_cents: val(c.id) }));
              const r = await run("r", () => put(g(`/payments/${payment.id}/allocations`), { allocations, reason }), "Affectation corrigée ; l’ancienne ventilation est conservée au journal.");
              if (r) onClose();
            }}
          >
            Enregistrer la correction
          </Button>
        </>
      }
    >
      {q.isLoading ? <Loading /> : null}
      <p>Paiement de {money(payment.amount_cents)} du {date(payment.received_on)}.</p>
      {calls.map((c: any) => (
        <div key={c.id} className="gestion-alloc">
          <span>
            {c.period_label} · reste dû {money(c.outstanding_cents)}
          </span>
          <MoneyInput label={`Affecter à ${c.period_label}`} cents={val(c.id)} onChange={(v) => setValues({ ...values, [c.id]: v })} />
        </div>
      ))}
      <p className="small">Non affecté : {money(payment.amount_cents - total)}</p>
      <Input label="Motif de la correction" value={reason} onChange={(e) => setReason(e.target.value)} required />
    </Modal>
  );
}

type PreviewRow = { line: number; booked_on: string | null; amount_cents: number | null; label: string; payer: string; reference: string; kind: string; error: string | null; duplicate: string | null; transaction_id: string | null };
type ImportPreview = { import_id: string; headers: string[]; mapping: any; rows: PreviewRow[]; summary: { total: number; duplicates: number; errors: number; debits: number } };

function ImportCsv({ onDone }: { onDone: () => void }) {
  const { g, can } = useG();
  const { busy, run } = useAction();
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [mapping, setMapping] = useState<any>(null);
  const [include, setInclude] = useState<string[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const history = useGet<{ items: any[] }>(g("/bank-imports"));
  const send = async (f: File, map?: any) => {
    setErr(null);
    if (preview) await post(g(`/bank-imports/${preview.import_id}/cancel`), { reason: "Aperçu remplacé" }).catch(() => undefined);
    const form = new FormData();
    form.append("file", f);
    if (map) form.append("mapping", JSON.stringify(map));
    try {
      const p = await upload<ImportPreview>(g("/bank-imports"), form);
      setPreview(p);
      setMapping(p.mapping);
      setInclude([]);
    } catch (e) {
      setErr(errMessage(e));
      setPreview(null);
    }
  };
  const col = (key: string, label: string, optional = true) => (
    <Select
      label={label}
      value={mapping?.[key] === null || mapping?.[key] === undefined ? "" : String(mapping[key])}
      onChange={(e) => setMapping({ ...mapping, [key]: e.target.value === "" ? null : Number(e.target.value) })}
      options={[...(optional ? [{ value: "", label: "Aucune" }] : []), ...(preview?.headers ?? []).map((h, i) => ({ value: String(i), label: `${i + 1}. ${h}` }))]}
    />
  );
  if (!can("finance")) return <Notice>Votre rôle ne permet pas d’importer des relevés.</Notice>;
  return (
    <div className="stack">
      <Card title="Importer un relevé">
        <p className="muted">
          Exportez vos opérations au format CSV depuis votre banque. Vous vérifiez l’aperçu avant import ; les doublons sont signalés ; les débits ne sont pas importés. L’import reste annulable.
        </p>
        <FileDrop
          accept=".csv,text/csv,text/plain"
          label="Choisir un fichier CSV"
          help="Fichier CSV (séparateur ; , ou tabulation), 2 Mo au maximum."
          busy={busy === "up"}
          onFiles={(fs) => {
            setFile(fs[0]);
            run("up", () => send(fs[0]));
          }}
        />
        {err ? <Notice tone="erreur">{err}</Notice> : null}
      </Card>
      {preview && mapping ? (
        <Card title="Colonnes du fichier">
          <div className="grid-3">
            {col("date_column", "Date", false)}
            {col("amount_column", "Montant (signé)")}
            {col("credit_column", "Crédit")}
            {col("debit_column", "Débit")}
            {col("label_column", "Libellé", false)}
            {col("payer_column", "Émetteur ou payeur")}
            {col("reference_column", "Référence")}
            <Select label="Format des dates" value={mapping.date_format} onChange={(e) => setMapping({ ...mapping, date_format: e.target.value })} options={["jj/mm/aaaa", "aaaa-mm-jj", "jj-mm-aaaa"].map((v) => ({ value: v, label: v }))} />
          </div>
          <Button variant="secondaire" busy={busy === "remap"} onClick={() => file && run("remap", () => send(file, mapping))}>
            Relire avec ces colonnes
          </Button>
        </Card>
      ) : null}
      {preview ? (
        <Card title="Aperçu avant import">
          <p>
            {preview.summary.total} ligne(s) : {preview.summary.duplicates} doublon(s) possible(s), {preview.summary.debits} débit(s) non importé(s), {preview.summary.errors} ligne(s) illisible(s).
          </p>
          <Table caption="Aperçu du relevé">
            <thead>
              <tr>
                <th>Ligne</th>
                <th>Date</th>
                <th>Libellé</th>
                <th className="right">Montant</th>
                <th>Constat</th>
              </tr>
            </thead>
            <tbody>
              {preview.rows.map((r) => (
                <tr key={r.line}>
                  <td>{r.line}</td>
                  <td>{r.booked_on ? date(r.booked_on) : "—"}</td>
                  <td>
                    {r.label}
                    {r.payer ? <span className="block small muted">{r.payer}</span> : null}
                  </td>
                  <td className="right">{r.amount_cents !== null ? <Amount cents={r.amount_cents} /> : "—"}</td>
                  <td>
                    {r.kind === "erreur" ? <Badge tone="erreur">{r.error}</Badge> : null}
                    {r.kind === "debit" ? <Badge>Débit : non importé</Badge> : null}
                    {r.duplicate && r.transaction_id ? (
                      <Checkbox
                        label={r.duplicate === "deja_importe" ? "Déjà importée : importer quand même" : "En double dans le fichier : importer quand même"}
                        checked={include.includes(r.transaction_id)}
                        onChange={(v) => setInclude(v ? [...include, r.transaction_id!] : include.filter((x) => x !== r.transaction_id))}
                      />
                    ) : null}
                    {r.kind === "credit" && !r.duplicate ? <Badge tone="succes">À importer</Badge> : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          <div className="gestion-actions">
            <Button
              busy={busy === "confirm"}
              onClick={async () => {
                const r = await run("confirm", () => post<any>(g(`/bank-imports/${preview.import_id}/confirm`), { include_duplicates: include }), (x: any) => `${x.imported} opération(s) importée(s), à rapprocher.`);
                if (r) {
                  setPreview(null);
                  onDone();
                }
              }}
            >
              Importer les opérations
            </Button>
            <Button
              variant="discret"
              onClick={async () => {
                await run("drop", () => post(g(`/bank-imports/${preview.import_id}/cancel`), { reason: "Aperçu abandonné" }));
                setPreview(null);
              }}
            >
              Abandonner
            </Button>
          </div>
        </Card>
      ) : null}
      <Card title="Imports précédents">
        {(history.data?.items ?? []).length === 0 ? <p className="muted">Aucun import.</p> : null}
        <ul className="gestion-list">
          {(history.data?.items ?? [])
            .filter((i) => i.status !== "apercu")
            .map((i) => (
              <li key={i.id} className="gestion-line">
                <span>
                  {i.filename} · {dateTime(i.created_at)} · {i.rows_imported} importée(s)
                  {i.status === "annule" ? <Badge tone="erreur">Annulé : {i.cancelled_reason}</Badge> : null}
                </span>
                <span className="gestion-actions">
                  <DocLink id={i.document_id}>Fichier d’origine</DocLink>
                  {i.status === "importe" ? (
                    <Button variant="discret" small busy={busy === i.id} onClick={() => run(i.id, () => post(g(`/bank-imports/${i.id}/cancel`), { reason: "Annulé par l’utilisateur" }), (x: any) => `Import annulé ; ${x.cancelled_payments} paiement(s) issu(s) du relevé annulé(s).`)}>
                      Annuler l’import
                    </Button>
                  ) : null}
                </span>
              </li>
            ))}
        </ul>
      </Card>
    </div>
  );
}
